import { useCallback, useEffect, useState } from 'react';

import { noop } from 'lodash';
import { useGrfqRfq } from '#/unified-rfqs/ui/rfq-context';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { useGrfqBboService } from '#/unified-rfqs/contexts/grfq-bbo';
import {
  ReadonlyObservable,
  createReadonlyObservable,
} from '@paradigm/utils/src/createObservable';
import { GrfqBboWithId } from '#/unified-rfqs/store/grfq-bbo';
import { useObservable } from '@paradigm/utils/src/hooks';

export interface BboState {
  readonly isLoading: boolean;
  readonly data: GrfqBbo | null;
  readonly error: string | null;
}

const [EMPTY_BBO] = createReadonlyObservable<GrfqBboWithId | null>(
  'empty-grfq-bbo',
  null,
);

export function useGrfqBbo() {
  const grfqBboService = useGrfqBboService();
  const rfq = useGrfqRfq();
  const [isLoading, setIsLoading] = useState(false);
  const [bboResult, setBboResult] = useState<BboResult>();
  const acquireBbo = useAcquireGrfqBbo();

  useEffect(() => {
    setIsLoading(true);
    acquireBbo(rfq.id)
      .then(setBboResult)
      .finally(() => setIsLoading(false));
  }, [acquireBbo, grfqBboService, rfq.id]);

  // Cleanup
  useEffect(() => {
    if (bboResult == null) return;
    return bboResult.release;
  }, [bboResult]);

  const data = useObservable(bboResult?.bbo ?? EMPTY_BBO);

  return {
    isLoading,
    data,
    error: bboResult?.error ?? null,
  };
}

interface BboResult {
  readonly release: () => void;
  readonly bbo: ReadonlyObservable<GrfqBboWithId | null>;
  readonly error: string | null;
}
type AcquireBbo = (rfqId: number) => Promise<BboResult>;

export function useAcquireGrfqBbo(): AcquireBbo {
  const grfqBboService = useGrfqBboService();
  const handleError = (error: Error) => ({
    release: noop,
    bbo: EMPTY_BBO,
    error: error.message,
  });
  return useCallback(
    async (rfqId: number) => {
      try {
        const resp = await grfqBboService.getGrfqBbo(rfqId);
        if (!resp.ok) {
          return handleError(new Error(resp.error));
        }
        grfqBboService.openRfqTickerChannel(rfqId);
        const release = () => {
          grfqBboService.closeRfqTickerChannel(rfqId);
        };
        const bbo = grfqBboService.getRfqBboObservable(rfqId);
        return { release, bbo, error: null } as const;
      } catch (error) {
        return handleError(error as Error);
      }
    },
    [grfqBboService],
  );
}
